import { useLoadState } from './load-state';

export const useFetchComposable = <TApiFunc extends (...args: any[]) => any>(
  apiFunc: TApiFunc,
  opts?: {
    minDisplayTime?: number; // default 0
  },
) => {
  type RawResponse = Awaited<ReturnType<TApiFunc>>;
  type RawData = RawResponse extends { data: infer D } ? D : RawResponse;

  type SuccessSendReturn = { isSuccess: true; data: RawData; error: null };
  type ErrorSendReturn = {
    isSuccess: false;
    data: null;
    error: RawResponse extends { error: infer E } ? E : unknown;
  };

  const { startLoad, endLoad, isLoading } = useLoadState();

  const send = async (
    ...args: Parameters<TApiFunc>
  ): Promise<SuccessSendReturn | ErrorSendReturn> => {
    const minTime = opts?.minDisplayTime ?? 0;
    const startTime = Date.now();

    startLoad();

    try {
      const response = await apiFunc(...args);

      const executionTime = Date.now() - startTime;
      const remainingTime = minTime - executionTime;

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      if (response && typeof response === 'object' && 'error' in response && response.error) {
        endLoad({ isError: true });
        return { isSuccess: false, data: null, error: response.error };
      }

      const rawData =
        response && typeof response === 'object' && 'data' in response ? response.data : response;

      endLoad({ isSuccess: true });
      return { isSuccess: true, data: rawData, error: null };
    } catch (error) {
      endLoad({ isError: true });
      return { isSuccess: false, data: null, error: error as ErrorSendReturn['error'] };
    }
  };

  return { isLoading, send };
};
